// Home.tsx – 스냅 제거, 일반 스크롤 + 애니메이션 유지
import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Button,
  Text,
  Stack,
  SimpleGrid,
  Heading,
  VStack,
  HStack,
  Badge,
  Icon,
  Link as ChakraLink,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useColorModeValue
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import Section from '../components/Section'
import {
  ArrowForwardIcon,
  ExternalLinkIcon,
  CheckCircleIcon
} from '@chakra-ui/icons'
import { keyframes } from '@emotion/react'
import { FaYoutube, FaChartLine } from 'react-icons/fa'

const MotionBox = motion(Box)
const MotionStack = motion(Stack)

/* ---------------- Small Components ---------------- */

type StepProps = { n: number; title: string; desc: string }
const Step: React.FC<StepProps> = ({ n, title, desc }) => (
  <HStack align="start" spacing={4}>
    <Box
      w="28px"
      h="28px"
      borderRadius="full"
      bg="purple.600"
      color="white"
      fontWeight="bold"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      {n}
    </Box>
    <VStack align="start" spacing={0}>
      <Text fontWeight="bold">{title}</Text>
      <Text color="gray.600" fontSize="sm">
        {desc}
      </Text>
    </VStack>
  </HStack>
)

const scrollWheel = keyframes`
  0% { transform: translateX(-50%) translateY(0); opacity: 1; }
  80% { transform: translateX(-50%) translateY(10px); opacity: 1; }
  81% { transform: translateX(-50%) translateY(0); opacity: 0; }
  100% { transform: translateX(-50%) translateY(0); opacity: 1; }
`

type ResourceCardProps = {
  title: string
  desc: string
  href: string
  icon: React.ElementType
  colorFrom: string
  colorTo: string
  cta?: string
}

const ResourceCard: React.FC<ResourceCardProps> = (props) => {
  const { t } = useTranslation()
  const { title, desc, href, icon, colorFrom, colorTo, cta } = props
  return (
    <MotionBox
      whileHover={{ y: -4, scale: 1.01 }}
      transition={{ type: 'spring', stiffness: 200, damping: 18 }}
      bgGradient={`linear(to-br, ${colorFrom}, ${colorTo})`}
      borderRadius="2xl"
      p={{ base: 4, md: 6 }}
      boxShadow="xl"
      position="relative"
      overflow="hidden"
    >
      <HStack align="start" spacing={4}>
        <Box
          bg="whiteAlpha.200"
          borderRadius="xl"
          p={3}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Icon as={icon} boxSize={7} color="white" />
        </Box>
        <VStack align="start" spacing={2} flex={1}>
          <Heading size={{ base: 'md', md: 'lg' }} color="white">
            {title}
          </Heading>
          <Text color="whiteAlpha.900" fontSize={{ base: 'sm', md: 'md' }}>
            {desc}
          </Text>
          <Button
            as={ChakraLink}
            href={href}
            isExternal
            rightIcon={<ExternalLinkIcon />}
            size="sm"
            mt={2}
            colorScheme="whiteAlpha"
            variant="outline"
            _hover={{ bg: 'whiteAlpha.200' }}
          >
            {cta ?? t('바로 가기')}
          </Button>
        </VStack>
      </HStack>
    </MotionBox>
  )
}

type BenefitProps = { text: string }
const Benefit: React.FC<BenefitProps> = ({ text }) => (
  <HStack spacing={3}>
    <CheckCircleIcon color="green.500" />
    <Text color="gray.700">{text}</Text>
  </HStack>
)

/* ---------------- Page ---------------- */

const Home: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  const gridColor = useColorModeValue('#eceff7', '#1f2430')

  return (
    <DefaultLayout>
      {/* ===== Hero (100vh 섹션) ===== */}
      <Box
        as="section"
        position="relative"
        minH="100vh"
        bg="gray.800"
        overflow="hidden"
      >
        {/* 배경 영상 */}
        <Box
          as="video"
          autoPlay
          muted
          loop
          playsInline
          position="absolute"
          top={0}
          left={0}
          w="100%"
          h="100%"
          style={{ objectFit: 'cover' }}
          zIndex={0}
          filter="blur(8px) brightness(0.8)"
        >
          <source src="/videos/b.mp4" type="video/mp4" />
        </Box>

        {/* 콘텐츠 */}
        <Box
          position="relative"
          zIndex={1}
          maxW="3xl"
          mx="auto"
          px={4}
          py={{ base: 16, md: 24 }}
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
        >
          <Stack spacing={4}>
            <Text
              fontSize={{ base: '1xl', md: '3xl' }}
              fontWeight="bold"
              color="white"
              fontFamily="'Satisfy'"
            >
              Xrplay
            </Text>

            <Text
              fontSize={{ base: '2xl', md: '4xl' }}
              fontWeight="bold"
              color="white"
            >
              {t('리플 전문 자동매매 플랫폼')}
            </Text>

            <Text fontSize={{ base: 'sm', md: 'lg' }} color="gray.300">
              {t('감정은 트레이딩의 적이다. 알고리즘은 감정을 모른다.')}
              <br />
              {t(
                '리플 홀더와 전략을 공유하고, 자동매매로 수익을 극대화하세요.'
              )}
            </Text>

            <Box textAlign="center">
              <Button
                mt={5}
                size="lg"
                px={8}
                w="200px"
                py={6}
                borderRadius="full"
                bgGradient="linear(to-r, #3B82F6, #9333EA)"
                color="white"
                fontWeight="bold"
                _hover={{
                  bgGradient: 'linear(to-r, #2563EB, #7E22CE)',
                  transform: 'scale(1.03)',
                  transition: '0.2s ease-in-out'
                }}
                onClick={() => {
                  auth.id ? navigate('/trading') : navigate('/signIn')
                }}
                rightIcon={<ArrowForwardIcon />}
              >
                {t('시작하기')}
              </Button>
            </Box>

            {/* Scroll Indicator */}
            <Box mt={12}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={2}
              >
                <Box
                  w="20px"
                  h="30px"
                  border="2px solid white"
                  borderRadius="14px"
                  position="relative"
                >
                  <Box
                    w="4px"
                    h="8px"
                    bg="white"
                    borderRadius="full"
                    position="absolute"
                    top="4px"
                    left="50%"
                    transform="translateX(-50%)"
                    animation={`${scrollWheel} 2.5s infinite ease-in-out`}
                  />
                </Box>
                <Text color="white" fontSize="sm">
                  {t('스크롤하여 더 보기')}
                </Text>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* ===== 섹션 2: 리소스 ===== */}
      <Box as="section" bg="gray.50" px={4} py={{ base: 12, md: 16 }}>
        <Box maxW="6xl" mx="auto">
          <VStack spacing={2} textAlign="center" mb={{ base: 6, md: 10 }}>
            <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
              {t('학습 & 시그널')}
            </Badge>
            <Heading size={{ base: 'lg', md: 'xl' }} color="gray.800">
              {t('실시간 시그널과 인사이트를 한 곳에서')}
            </Heading>
            <Text color="gray.600">
              {t(
                '홈에서 바로 트레이딩뷰 시그널 보조지표와 XRP 전문 유튜브 채널을 확인하세요.'
              )}
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <ResourceCard
              title={t('트레이딩뷰 시그널 보조지표')}
              desc={t(
                '전략 설계에 도움 되는 커뮤니티 스크립트 페이지로 이동합니다.'
              )}
              href="https://kr.tradingview.com/script/dUkRohgK/"
              icon={FaChartLine}
              colorFrom="#4F46E5"
              colorTo="#9333EA"
              cta={t('트레이딩뷰 열기')}
            />
            <ResourceCard
              title={t('리스타트 피플 – XRP 유튜브')}
              desc={t(
                'XRP 중심의 시황, 매크로, 전략 인사이트를 영상으로 빠르게 학습.'
              )}
              href="https://www.youtube.com/@%EB%A6%AC%EC%8A%A4%ED%83%80%ED%8A%B8%ED%94%BC%ED%94%8C"
              icon={FaYoutube}
              colorFrom="#DC2626"
              colorTo="#F59E0B"
              cta={t('채널 보기')}
            />
          </SimpleGrid>

          {/* 하단 CTA 밴드 (글로잉 블럽은 제거해서 성능/호환성↑) */}
          <Box mt={{ base: 8, md: 10 }}>
            <MotionBox
              borderRadius="2xl"
              p={{ base: 5, md: 8 }}
              bgGradient="linear(to-r, #4F46E5, #9333EA)"
              color="white"
              whileHover={{ scale: 1.01 }}
              transition={{ type: 'spring', stiffness: 200, damping: 20 }}
            >
              <HStack
                spacing={{ base: 4, md: 8 }}
                align="center"
                justify="space-between"
                flexDir={{ base: 'column', md: 'row' }}
              >
                <Heading size={{ base: 'md', md: 'lg' }}>
                  {t('5분 만에 자동매매 시작해보기')}
                </Heading>
                <HStack spacing={4}>
                  <Button
                    onClick={() =>
                      auth.id ? navigate('/trading') : navigate('/signIn')
                    }
                    rightIcon={<ArrowForwardIcon />}
                    colorScheme="whiteAlpha"
                    variant="outline"
                  >
                    {t('지금 시작')}
                  </Button>
                </HStack>
              </HStack>
            </MotionBox>

            {/* 링크 버튼 */}
            <HStack spacing={4} mt={6} justify="center">
              <Button
                as={ChakraLink}
                href="https://kr.tradingview.com/script/dUkRohgK/"
                isExternal
                variant="ghost"
                rightIcon={<ExternalLinkIcon />}
              >
                {t('보조지표 보기')}
              </Button>
              <Button
                as={ChakraLink}
                href="https://www.youtube.com/@%EB%A6%AC%EC%8A%A4%ED%83%80%ED%8A%B8%ED%94%BC%ED%94%8C"
                isExternal
                variant="ghost"
                rightIcon={<ExternalLinkIcon />}
              >
                {t('유튜브 바로가기')}
              </Button>
            </HStack>
          </Box>
        </Box>
      </Box>

      {/* ===== 섹션 3: 왜 Xrplay인가 ===== */}
      <Box
        as="section"
        bg="white"
        position="relative"
        px={4}
        py={{ base: 12, md: 16 }}
      >
        {/* 은은한 배경 그리드 */}
        <Box
          position="absolute"
          inset={0}
          bgImage={`linear-gradient(${gridColor} 1px, transparent 1px), linear-gradient(90deg, ${gridColor} 1px, transparent 1px)`}
          bgSize="40px 40px, 40px 40px"
          opacity={0.25}
          pointerEvents="none"
        />
        <Box position="relative" maxW="6xl" mx="auto">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
            <MotionBox
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 250, damping: 20 }}
            >
              <VStack align="start" spacing={4}>
                <Heading size="md">{t('감정 배제')}</Heading>
                <Text color="gray.600">
                  {t(
                    '알고리즘 신호에 따라 일관된 실행. 일시적 공포/탐욕에서 자유.'
                  )}
                </Text>
                <Benefit text={t('사전에 정의한 진입/청산 규칙')} />
                <Benefit text={t('리스크/포지션 사이징 자동화')} />
              </VStack>
            </MotionBox>
            <MotionBox
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 250, damping: 20 }}
            >
              <VStack align="start" spacing={4}>
                <Heading size="md">{t('학습 곡선 단축')}</Heading>
                <Text color="gray.600">
                  {t('검증된 리소스와 커뮤니티 자료로 빠르게 전략 이해.')}
                </Text>
                <Benefit text={t('트레이딩뷰 스크립트 연동 가이드')} />
                <Benefit text={t('XRP 특화 인사이트 영상')} />
              </VStack>
            </MotionBox>
            <MotionBox
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 250, damping: 20 }}
            >
              <VStack align="start" spacing={4}>
                <Heading size="md">{t('즉시 시작')}</Heading>
                <Text color="gray.600">
                  {t('회원가입 후 API 연결만 하면 자동으로 운용 시작.')}
                </Text>
                <Benefit text={t('5분 만에 셋업 완료')} />
                <Benefit text={t('백테스트 프리셋 제공(옵션)')} />
              </VStack>
            </MotionBox>
          </SimpleGrid>

          {/* 빠른 시작 3단계 */}
          <Box mt={10}>
            <Heading size="md" mb={4}>
              {t('3단계로 시작하기')}
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Step
                n={1}
                title={t('거래소 가입')}
                desc={t('이메일/소셜로 몇 초 만에 계정 생성')}
              />
              <Step
                n={2}
                title={t('API 연결')}
                desc={t('거래소 API 키 입력 (출금 권한 불필요)')}
              />
              <Step
                n={3}
                title={t('전략 선택')}
                desc={t('프리셋/사용자 전략 적용 후 자동매매 실행')}
              />
            </SimpleGrid>
          </Box>

          {/* 하단 프로세스 + Stats */}
          <Box mt={{ base: 10, md: 12 }}>
            <Heading size="sm" mb={4} color="gray.700">
              {t('운영 프로세스')}
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
              {[
                { t: t('신호 발생'), d: t('지표/조건 충족 즉시 트리거') },
                { t: t('주문 실행'), d: t('시장가 주문·TP/SL 동시 설정') },
                { t: t('모니터링'), d: t('청산가·시황 모니터링') },
                { t: t('업데이트'), d: t('백데이터를 통해 전략 제공') }
              ].map((item, i) => (
                <MotionBox
                  key={i}
                  p={5}
                  borderRadius="xl"
                  bg="white"
                  boxShadow="md"
                  whileHover={{ y: -4, scale: 1.01 }}
                >
                  <Badge colorScheme="purple" mb={2}>
                    {i + 1}
                  </Badge>
                  <Heading size="sm" mb={1}>
                    {item.t}
                  </Heading>
                  <Text color="gray.600" fontSize="sm">
                    {item.d}
                  </Text>
                </MotionBox>
              ))}
            </SimpleGrid>
          </Box>

          <Box
            bg="gray.900"
            color="white"
            mt={{ base: 10, md: 12 }}
            borderRadius="xl"
            py={{ base: 6, md: 8 }}
          >
            <Box maxW="6xl" mx="auto" px={4}>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                {[
                  { k: t('전략 프리셋'), v: '5+' },
                  { k: t('평균 체결 속도'), v: '< 200ms' },
                  { k: t('지원 거래소'), v: t('2곳') },
                  { k: t('커뮤니티 회원'), v: '4k+' }
                ].map((s, i) => (
                  <VStack key={i} spacing={1}>
                    <Heading size="lg">{s.v}</Heading>
                    <Text opacity={0.8}>{s.k}</Text>
                  </VStack>
                ))}
              </SimpleGrid>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ===== 섹션 4: FAQ ===== */}
      <Box as="section" bg="gray.50" px={4} py={{ base: 12, md: 16 }}>
        <Box maxW="6xl" mx="auto">
          <Heading size="md" mb={4}>
            {t('자주 묻는 질문')}
          </Heading>
          <Accordion allowMultiple borderRadius="xl">
            {[
              {
                q: t('거래소 연동은 안전한가요?'),
                a: t(
                  '선물/주문 권한 API 키만 사용하며, 출금 권한은 요구하지 않습니다. IP 제한/2FA 권장.'
                )
              },
              {
                q: t('전략은 어떻게 선택하나요?'),
                a: t(
                  '프리셋/사용자 전략 모두 지원. 백테스트와 소액 실계정 시뮬 권장.'
                )
              },
              {
                q: t('요금은 어떻게 되나요?'),
                a: t('무료 체험 후 구독 전환. 프로모션 기간 할인 제공.')
              },
              {
                q: t('진입이 되지 않아요.'),
                a: t(
                  '선물 지갑에 USDT가 있는지, 현물에서 Transfer 했는지 확인해 주세요.'
                )
              },
              {
                q: t('자동으로 종료되었어요.'),
                a: t(
                  '이용권 만료 또는 API 오류 시 안전을 위해 자동 종료됩니다.'
                )
              }
            ].map((item, idx) => (
              <AccordionItem
                key={idx}
                bg="white"
                borderRadius="xl"
                mb={3}
                boxShadow="sm"
              >
                <h2>
                  <AccordionButton _expanded={{ bg: 'gray.100' }}>
                    <Box as="span" flex="1" textAlign="left" fontWeight="bold">
                      {item.q}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4} color="gray.600">
                  {item.a}
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>

          {/* 하단 CTA */}
          <Box mt={{ base: 8, md: 10 }}>
            <MotionBox
              p={6}
              bgGradient="linear(to-r, #4F46E5, #9333EA)"
              color="white"
              borderRadius="2xl"
              boxShadow="lg"
              whileHover={{ scale: 1.01 }}
            >
              <Heading size="md" mb={2}>
                {t('시작할 준비 되셨나요?')}
              </Heading>
              <Text opacity={0.9} mb={4}>
                {t('거래소 회원가입 → API 연결 → 자동매매까지 단 3단계.')}
              </Text>
              <HStack spacing={4}>
                <Button
                  onClick={() =>
                    auth.id ? navigate('/trading') : navigate('/signIn')
                  }
                  rightIcon={<ArrowForwardIcon />}
                  colorScheme="whiteAlpha"
                  variant="outline"
                >
                  {t('지금 시작')}
                </Button>
                <Button
                  as={ChakraLink}
                  href="https://www.youtube.com/@%EB%A6%AC%EC%8A%A4%ED%83%80%ED%8A%B8%ED%94%BC%ED%94%8C"
                  isExternal
                  colorScheme="blackAlpha"
                >
                  {t('영상 살펴보기')}
                </Button>
              </HStack>
            </MotionBox>
          </Box>
        </Box>
      </Box>

      {/* ===== 섹션 5: 자동매매 소개 ===== */}
      <Box
        as="section"
        px={4}
        py={{ base: 12, md: 16 }}
        bgGradient="linear(to-b, white, #f5f6ff 58.5%)"
      >
        <Box maxW="6xl" mx="auto">
          <Box textAlign="center" mt={2}>
            <Box
              display="inline-block"
              bg="#F3E5F5"
              color="#6A1B9A"
              px={4}
              py={2}
              borderRadius="full"
              fontWeight="bold"
              fontSize="lg"
            >
              {t('자동매매')}
            </Box>
          </Box>

          <Text
            fontSize={{ base: 'md', md: '3xl' }}
            color="gray.700"
            mt={4}
            mb={10}
            textAlign={'center'}
          >
            {t('직장인, 주부, 대학생 등 누구나 쉽게 시작할 수 있어요')}
          </Text>

          <Section />
        </Box>
      </Box>
      {/* Footer는 DefaultLayout 안에서 자동으로 붙음 (이제 정상 노출) */}
    </DefaultLayout>
  )
}

export default Home
